import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Building2, Stethoscope, Briefcase, CheckCircle2, Users, Award, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';
import { generateFAQSchema, generateBreadcrumbSchema, schemaToString } from '../lib/schemas';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const HOSPITALET_FAQS = [
  {
    question: '¿Trabajáis con empresas de L\'Hospitalet de Llobregat?',
    answer: 'Sí, L\'Hospitalet es una de nuestras áreas de mayor actividad. Es la segunda ciudad más grande de Cataluña y tiene un tejido empresarial muy dinámico. Tenemos casos de éxito verificables en empresas de reformas de L\'Hospitalet.'
  },
  {
    question: '¿Qué tipo de transformación digital ofrecéis en L\'Hospitalet?',
    answer: 'Desde automatización de procesos y agentes de IA hasta implementación de ERP y CRM. En L\'Hospitalet trabajamos principalmente con empresas de servicios, reformas, comercio y clínicas del área metropolitana.'
  },
  {
    question: '¿Podéis implementar MANDO para una empresa de reformas de L\'Hospitalet?',
    answer: 'Sí. MANDO by Leapifyes es nuestro ERP para empresas de reformas y construcción, y tenemos clientes en L\'Hospitalet que han aumentado su capacidad de gestión en 3 obras más al mes sin contratar más personal.'
  },
  {
    question: '¿Cuánto tarda la implementación en L\'Hospitalet?',
    answer: 'Los primeros resultados se ven en 1-2 semanas (quick wins). Un proyecto completo de transformación digital en L\'Hospitalet suele durar entre 4 y 10 semanas dependiendo del alcance.'
  },
  {
    question: '¿Ofrecéis atención presencial en L\'Hospitalet?',
    answer: 'Sí. Al estar L\'Hospitalet junto a Barcelona, la atención presencial es muy conveniente. Podemos reunirnos en tus instalaciones para el diagnóstico, el arranque y las sesiones de formación.'
  }
];

const Hospitalet = () => {
  const breadcrumbs = [
    { name: 'Inicio', path: '/' },
    { name: "L'Hospitalet", path: '/hospitalet' }
  ];

  const zones = ['Centre', 'Collblanc', 'La Torrassa', 'Bellvitge', 'Gornal', 'Santa Eulàlia', 'Les Planes', 'Granvia Sud'];

  return (
    <Layout>
      <SEO path="/hospitalet" />

      <script type="application/ld+json">
        {schemaToString(generateBreadcrumbSchema(breadcrumbs))}
      </script>
      <script type="application/ld+json">
        {schemaToString(generateFAQSchema(HOSPITALET_FAQS))}
      </script>

      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0"><div className="mesh-gradient opacity-40" /><div className="grid-pattern absolute inset-0 opacity-20" /></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#1B93A4]/10 to-[#D946EF]/10 rounded-full blur-3xl" />

        <div className="container-main relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }}>
              <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-6">
                <MapPin className="w-5 h-5 text-[#1B93A4]" />
                <span className="text-sm font-medium text-[#8892A4]">L'Hospitalet de Llobregat · Barcelonès</span>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-5xl md:text-6xl font-bold text-[#F0F4FF] mb-6 leading-tight">
                Transformación Digital en <span className="gradient-text">L'Hospitalet</span>
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-xl text-[#8892A4] mb-8 leading-relaxed">
                Digitalizamos empresas de L'Hospitalet de Llobregat con soluciones de IA, automatización y ERP a medida. Más eficiencia, más clientes, menos caos operativo.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
                <a href="https://ricardoserrano-leapifyes.zohobookings.eu/#/242621000000571012" target="_blank" rel="noopener noreferrer">
                  <Button className="btn-gradient text-base px-8 py-6">
                    Diagnóstico Gratuito <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <Link to="/soluciones">
                  <Button variant="outline" className="text-base px-8 py-6 border-white/10 text-[#F0F4FF] hover:bg-white/5">
                    Ver Soluciones
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              <img
                src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800&q=80"
                alt="Transformación digital en L'Hospitalet de Llobregat"
                className="rounded-2xl w-full h-[450px] object-cover border border-white/10"
                loading="lazy"
                width="800"
                height="450"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding border-t border-white/5">
        <div className="container-main">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              { icon: Users, value: '+50', label: 'Empresas transformadas' },
              { icon: MapPin, value: '100%', label: "Atención local L'Hospitalet" },
              { icon: Award, value: '+3', label: 'Obras/mes más (reformas)' },
              { icon: Clock, value: '<24h', label: 'Tiempo respuesta' },
            ].map((stat, i) => (
              <Card key={i} className="border-white/10 bg-[#161B22]">
                <CardContent className="p-6 text-center">
                  <stat.icon className="w-8 h-8 mx-auto mb-4 text-[#1B93A4]" />
                  <p className="text-3xl font-bold gradient-text">{stat.value}</p>
                  <p className="text-[#8892A4] text-sm">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#F0F4FF] mb-6">¿Por qué Leapifyes en L'Hospitalet?</h2>
              <ul className="space-y-4">
                {[
                  "Casos de éxito reales en empresas de L'Hospitalet",
                  'Proximidad y disponibilidad presencial inmediata',
                  'Soporte en castellano, catalán e inglés',
                  "Especialistas en el tejido empresarial del Barcelonès",
                  'ROI medible en semanas, no en años',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#1B93A4] flex-shrink-0" />
                    <span className="text-[#8892A4]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#F0F4FF] mb-4">Zonas que atendemos</h3>
              <div className="flex flex-wrap gap-2">
                {zones.map((zone, i) => (
                  <span key={i} className="px-4 py-2 bg-white/5 rounded-full text-[#8892A4] text-sm">{zone}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sectores */}
      <section className="section-padding border-t border-white/5">
        <div className="container-main">
          <h2 className="text-4xl font-bold text-[#F0F4FF] mb-8 text-center">Sectores que digitalizamos en L'Hospitalet</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Building2, title: 'Reformas y Construcción', desc: 'ERP MANDO para obras, presupuestos y control financiero' },
              { icon: Stethoscope, title: 'Clínicas y Salud', desc: 'Automatización de citas, recordatorios y gestión de pacientes' },
              { icon: Briefcase, title: 'Comercio y Servicios', desc: 'CRM, agentes IA y omnicanal para negocios de L\'Hospitalet' },
            ].map((sector, i) => (
              <Card key={i} className="border-white/10 bg-[#161B22] card-hover">
                <CardContent className="p-6">
                  <sector.icon className="w-10 h-10 text-[#3B82F6] mb-4" />
                  <h3 className="text-xl font-bold text-[#F0F4FF] mb-2">{sector.title}</h3>
                  <p className="text-[#8892A4]">{sector.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding border-t border-white/5">
        <div className="container-main">
          <h2 className="text-4xl font-bold text-[#F0F4FF] mb-8 text-center">Preguntas Frecuentes — L'Hospitalet</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {HOSPITALET_FAQS.map((faq, i) => (
              <div key={i} className="bg-[#161B22] rounded-xl p-6 border border-white/10">
                <h3 className="text-lg font-bold text-[#F0F4FF] mb-2">{faq.question}</h3>
                <p className="text-[#8892A4]">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-main text-center">
          <div className="glass-card p-12 rounded-2xl">
            <h2 className="text-4xl font-bold text-[#F0F4FF] mb-4">¿Empezamos tu transformación en L'Hospitalet?</h2>
            <p className="text-xl text-[#8892A4] mb-8 max-w-2xl mx-auto">Diagnóstico gratuito de 30 minutos. Sin compromiso, sin tecnicismos.</p>
            <a href="https://ricardoserrano-leapifyes.zohobookings.eu/#/242621000000571012" target="_blank" rel="noopener noreferrer">
              <Button className="btn-gradient text-base px-8 py-6">Agendar Diagnóstico <ArrowRight className="w-5 h-5 ml-2" /></Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Hospitalet;
